/**
 * Transfer between legal entities — the request and response shapes.
 *
 * The three routes themselves live in `employeeContract` (`transfer`, `previewTransfer`,
 * `cancelTransfer`, all `routeMeta('employment.transfer:execute')`); this file holds their schemas so
 * `employee.contract.ts` stays readable and so the Employee DTO can name the link between the two
 * employments without importing the whole router. Design: docs/plans/legal-entity-transfer/PLAN.md.
 *
 * A transfer ENDS one Employment (the old company's, `lastWorkingDay` the day before, exit type
 * `transfer`) and STARTS another (the new company's, joined on the effective date) for the SAME
 * Person. Money is a decimal string with its currency, never a number (CLAUDE.md invariant 2).
 */

import { z } from 'zod';
import {
  currencySchema,
  dateOnlySchema,
  decimalStringSchema,
  employmentStatusSchema,
  employmentTypeSchema,
  uuidSchema,
  workTypeSchema,
} from '../schemas/common.js';
import { TRANSFER_BLOCKER_CODES } from '../lib/employee-transfer.js';

/**
 * One end of a transfer, as the other end sees it. On the NEW employment this is where the person
 * came from (`transferredFrom`); on the OLD one it is where they went (`transferredTo`).
 */
export const transferLinkSchema = z.object({
  employmentId: uuidSchema,
  employeeCode: z.string(),
  legalEntityId: uuidSchema,
  legalEntityName: z.string(),
  /** The day the move takes (took) effect: the new employment's first day. */
  effectiveOn: dateOnlySchema,
  /** True while that day has not arrived in the person's own time zone: it can still be cancelled. */
  pending: z.boolean(),
});
export type TransferLink = z.infer<typeof transferLinkSchema>;

// ---------------------------------------------------------------------------
// Preview
// ---------------------------------------------------------------------------

export const previewTransferQuerySchema = z.object({
  /** The company they are moving to. */
  legalEntityId: uuidSchema,
  /** Defaults to the first day of next month in the person's own time zone. */
  effectiveOn: dateOnlySchema.optional(),
});

export const transferBlockerSchema = z.object({
  code: z.enum(TRANSFER_BLOCKER_CODES),
  message: z.string(),
});

export const transferWarningSchema = z.object({
  /** `mid_month`, `currency_change`, `status_probation` ... free-form on purpose: warnings never block. */
  code: z.string(),
  message: z.string(),
});

const transferPersonRefSchema = z.object({
  employmentId: uuidSchema,
  employeeCode: z.string(),
  name: z.string(),
});

const transferNamedRefSchema = z.object({ id: uuidSchema, name: z.string() });

export const transferPreviewSchema = z.object({
  employmentId: uuidSchema,
  personId: uuidSchema,
  effectiveOn: dateOnlySchema,
  /** The old company's last working day: the day before `effectiveOn`. */
  lastWorkingDayInOldCompany: dateOnlySchema,
  from: z.object({
    legalEntityId: uuidSchema,
    legalEntityName: z.string(),
    currency: currencySchema,
    employeeCode: z.string(),
    status: employmentStatusSchema,
    employmentType: employmentTypeSchema,
  }),
  to: z.object({
    legalEntityId: uuidSchema,
    legalEntityName: z.string(),
    currency: currencySchema,
    country: z.string(),
  }),
  /** Reasons the transfer would be refused as entered. Empty = the wizard may confirm. */
  blockers: z.array(transferBlockerSchema),
  /** Things worth knowing that do not block (a mid-month date, a probation that continues). */
  warnings: z.array(transferWarningSchema),
  /** From the new company's ID series; null = no series, HR must type a code. */
  suggestedEmployeeCode: z.string().nullable(),
  /** True when the pay currency differs, so the salary cannot be pre-filled. */
  currencyChanges: z.boolean(),
  /**
   * Today's pay, only for a caller who may read compensation; null otherwise (and the wizard then
   * leaves the salary for HR to type).
   */
  currentCompensation: z
    .object({
      currency: currencySchema,
      basicSalary: decimalStringSchema.nullable(),
      salaryPerHour: decimalStringSchema.nullable(),
    })
    .nullable(),
  /** People who report to them now (first 50; `directReportsTotal` is the full count). */
  directReports: z.array(transferPersonRefSchema),
  directReportsTotal: z.number().int().nonnegative(),
  /** Departments, branches and cost centres they head or own. */
  headships: z.object({
    departments: z.array(transferNamedRefSchema),
    locations: z.array(transferNamedRefSchema),
    costCentres: z.array(transferNamedRefSchema),
  }),
  /** Role grants limited to the old company or its branches: HR reviews, nothing is changed. */
  roleGrants: z.array(
    z.object({
      assignmentId: uuidSchema,
      roleKey: z.string(),
      roleName: z.string(),
      scope: z.string(),
    }),
  ),
  /** Leave types of the old company that do not exist in the new one (balances still carry over). */
  leaveTypesMissingInNewCompany: z.array(transferNamedRefSchema),
  /** Open items HR must tick as reviewed; nothing is cancelled or settled automatically. */
  openItems: z.object({
    expenseClaims: z.number().int().nonnegative(),
    salaryAdvances: z.number().int().nonnegative(),
    loans: z.number().int().nonnegative(),
    loanRequests: z.number().int().nonnegative(),
    pendingLeaveRequests: z.number().int().nonnegative(),
    pendingProfileRequests: z.number().int().nonnegative(),
    /** True when any of the above is above zero: `reviewedOpenItems` must then be sent as true. */
    requiresReview: z.boolean(),
  }),
  /** Dated changes already scheduled on the old record after `effectiveOn` (carried over). */
  futureChanges: z.array(z.object({ validFrom: dateOnlySchema, summary: z.string() })),
  /** Salary revisions already dated after `effectiveOn` (carry them across or drop them). */
  futureSalaryRevisions: z.array(
    z.object({
      validFrom: dateOnlySchema,
      currency: currencySchema,
      basicSalary: decimalStringSchema.nullable(),
    }),
  ),
  payroll: z.object({
    /** Last period end already finalized for the OLD company, null when none. */
    oldCompanyFinalizedThrough: dateOnlySchema.nullable(),
    /** Same for the NEW company. */
    newCompanyFinalizedThrough: dateOnlySchema.nullable(),
    /** The date is not the 1st: that month's pay is split across two companies. */
    midMonth: z.boolean(),
  }),
  /** A transfer of this person already waiting for its date, if any. */
  pendingTransfer: transferLinkSchema.nullable(),
});
export type TransferPreview = z.infer<typeof transferPreviewSchema>;

// ---------------------------------------------------------------------------
// Execute
// ---------------------------------------------------------------------------

export const transferEmployeeBodySchema = z.object({
  /** The company they are moving to. */
  legalEntityId: uuidSchema,
  /** The new employment's first day. The old one ends the day before. Today or earlier is allowed only into an open payroll period. */
  effectiveOn: dateOnlySchema,
  /** Typed code; omitted = allocate the next one from the new company's ID series. */
  employeeCode: z.string().trim().min(1).max(50).optional(),
  /** Their branch in the new company; must belong to it. */
  locationId: uuidSchema,
  departmentId: uuidSchema.nullable().optional(),
  designation: z.string().trim().max(200).nullable().optional(),
  jobRole: z.string().trim().max(200).nullable().optional(),
  workType: workTypeSchema.nullable().optional(),
  /** Catalogue grade / cost centre / business unit of the NEW company; null = none. */
  gradeId: uuidSchema.nullable().optional(),
  costCentreId: uuidSchema.nullable().optional(),
  businessUnitId: uuidSchema.nullable().optional(),
  /** Their manager in the new company (an employment id); null = none. A manager in another company is allowed. */
  managerEmploymentId: uuidSchema.nullable().optional(),
  /** Omitted = keep the current type. */
  employmentType: employmentTypeSchema.optional(),
  /**
   * Pay in the new company. Omitted = copy today's pay when the currency is the same; a different
   * currency requires it (422 `salary_required`). The wizard's "% increase" is computed in the browser.
   */
  compensation: z
    .object({
      currency: currencySchema,
      basicSalary: decimalStringSchema.optional(),
      salaryPerHour: decimalStringSchema.optional(),
    })
    .refine((value) => value.basicSalary !== undefined || value.salaryPerHour !== undefined, {
      message: 'Provide at least one of basicSalary or salaryPerHour',
    })
    .optional(),
  /** Their direct reports move to the new employment (default) or stay under the old record. */
  moveDirectReports: z.boolean().default(true),
  /** Departments they head, branches they head and cost centres they own move with them (default) or stay. */
  moveHeadships: z.boolean().default(true),
  /** Salary revisions already dated after the effective date: carry them (same currency only) or drop them. */
  futureSalaryRevisions: z.enum(['carry', 'drop']).default('carry'),
  /** HR ticked "I have reviewed the open claims, advances, loans and leave requests" (required when the preview lists any). */
  reviewedOpenItems: z.boolean().default(false),
  reason: z.string().trim().max(500).optional(),
});
export type TransferEmployeeBody = z.infer<typeof transferEmployeeBodySchema>;

export const transferResultSchema = z.object({
  fromEmploymentId: uuidSchema,
  toEmploymentId: uuidSchema,
  personId: uuidSchema,
  effectiveOn: dateOnlySchema,
  /** The new employment's code. */
  employeeCode: z.string(),
  /** True when the date has not arrived: the new record waits as `pre_joining` and can be cancelled. */
  pending: z.boolean(),
  moved: z.object({
    directReports: z.number().int().nonnegative(),
    departments: z.number().int().nonnegative(),
    locations: z.number().int().nonnegative(),
    costCentres: z.number().int().nonnegative(),
    customFieldValues: z.number().int().nonnegative(),
    futureChanges: z.number().int().nonnegative(),
    futureSalaryRevisions: z.number().int().nonnegative(),
  }),
});
export type TransferResult = z.infer<typeof transferResultSchema>;

export const cancelTransferBodySchema = z.object({
  reason: z.string().trim().max(500).optional(),
});
